import React, { FC, useEffect, useState } from 'react';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTypedSelector } from '../../../../store/store';
import { g } from '../../HomeScreen/HomeScreen.styles';

const CIRCUMFERENCE = 2 * Math.PI * 58; // ~364.4
const FADE_DELAY_MS = 600; // hold at 100% before fading out

const arrowNudge = keyframes`
  0%, 100% { transform: translateY(0); opacity: 1; }
  50% { transform: translateY(-4px); opacity: 0.92; }
`;

const RingContainer = styled(Box)<{ $fadeOut?: boolean }>(({ $fadeOut }) => ({
  // Mockup: ~110px on a phone-width ticket.
  width: `max(96px, ${g(196)})`,
  height: `max(96px, ${g(196)})`,
  margin: '0 auto',
  position: 'relative',
  display: 'grid',
  placeItems: 'center',
  opacity: $fadeOut ? 0 : 1,
  transition: 'opacity 500ms ease',
  // Direct-child selector — must not cascade onto the arrow SVG nested inside
  // <Arrow>, otherwise the -90° rotation here rotates the arrow too, turning
  // it into a left-pointing arrow.
  '& > svg': {
    width: '100%',
    height: '100%',
    transform: 'rotate(-90deg)',
  },
}));

const Arrow = styled(Box)({
  position: 'absolute',
  width: '42%',
  height: '42%',
  display: 'grid',
  placeItems: 'center',
  animation: `${arrowNudge} 900ms ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  '& svg': {
    width: '74%',
    height: '74%',
  },
});

/** Upload progress in the brand color. */
export const ProgressRing: FC = () => {
  const brandColor = useTheme().palette.primary.main;
  const { uploadingProgress } = useTypedSelector((state) => state.upload);
  const offset = CIRCUMFERENCE - (uploadingProgress / 100) * CIRCUMFERENCE;
  const [fadeOut, setFadeOut] = useState(false);

  const trackColor = alpha(brandColor, 0.14);
  const fillColor = brandColor;

  useEffect(() => {
    if (uploadingProgress >= 100) {
      const timer = setTimeout(() => setFadeOut(true), FADE_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [uploadingProgress]);

  return (
    <RingContainer $fadeOut={fadeOut}>
      <svg viewBox="0 0 160 160" role="img" aria-label="Uploading">
        <circle
          cx="80"
          cy="80"
          r="58"
          style={{
            stroke: trackColor,
            strokeWidth: 14,
            fill: 'none',
          }}
        />
        <circle
          cx="80"
          cy="80"
          r="58"
          style={{
            stroke: fillColor,
            strokeWidth: 14,
            fill: 'none',
            strokeLinecap: 'round',
            strokeDasharray: CIRCUMFERENCE,
            strokeDashoffset: offset,
            transition: 'stroke-dashoffset 300ms ease',
          }}
        />
      </svg>
      <Arrow sx={{ color: fillColor }}>
        {/* Inline SVG instead of the Material Symbols web font — that font
            paints its text fallback ("arrow_upward") for the few hundred ms
            it takes to load on a fresh visit, which was the upward-arrow
            flash users were reporting. SVG renders on the first paint. */}
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 4l-8 8h5v8h6v-8h5z" />
        </svg>
      </Arrow>
    </RingContainer>
  );
};
